import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

/**
 * Demo Preview Context — Phase D1 (Feb 2026).
 *
 * Lives ABOVE AuthContext in the React tree so that:
 *  - When a Demo Token is present, the API interceptor automatically
 *    routes ALL requests with the demo Authorization header (see
 *    `/app/frontend/src/lib/api.js`).
 *  - `AuthContext.refresh()` will see the synthetic identity returned
 *    by `/auth/me` and render the rest of the app as that profile.
 *
 * Persistence: the demo token + the admin's stash of their real token
 * live in localStorage under separate keys, so the admin can exit
 * demo and instantly resume their normal admin session.
 */

const DemoPreviewContext = createContext({});

const STORAGE_DEMO_TOKEN = "tsw_demo_token";
const STORAGE_DEMO_IDENTITY = "tsw_demo_identity";
const STORAGE_DEMO_SESSION = "tsw_demo_session";
const STORAGE_ADMIN_TOKEN_STASH = "tsw_admin_token_stash";
const STORAGE_ADMIN_USER_STASH = "tsw_admin_user_stash";

const safeJSON = (raw) => { try { return JSON.parse(raw); } catch { return null; } };

export const DemoPreviewProvider = ({ children }) => {
  const [featureFlag, setFeatureFlag] = useState({ enabled: false, can_use: false, loading: true });
  const [identity, setIdentity] = useState(() => safeJSON(localStorage.getItem(STORAGE_DEMO_IDENTITY)));
  const [session, setSession] = useState(() => safeJSON(localStorage.getItem(STORAGE_DEMO_SESSION)));

  const isActive = Boolean(localStorage.getItem(STORAGE_DEMO_TOKEN));

  // Probe the backend once after the admin's session is established.
  // Cheap GET — returns booleans only. Hidden when can_use=false.
  const refreshFlag = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/demo-preview/feature-flag", { useRealToken: true });
      setFeatureFlag({ ...data, loading: false });
    } catch {
      setFeatureFlag({ enabled: false, can_use: false, loading: false });
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("tsw_token");
    if (token) refreshFlag();
    else setFeatureFlag({ enabled: false, can_use: false, loading: false });
  }, [refreshFlag]);

  const listIdentities = useCallback(async (filters = {}) => {
    const { data } = await api.get("/admin/demo-preview/identities", {
      params: filters,
      useRealToken: true,
    });
    return data.items || [];
  }, []);

  const startSession = useCallback(async (demoIdentityId) => {
    // 1) Stash the admin's real token + user so we can restore on exit.
    const realToken = localStorage.getItem("tsw_token");
    const realUser = localStorage.getItem("tsw_user");
    if (realToken) localStorage.setItem(STORAGE_ADMIN_TOKEN_STASH, realToken);
    if (realUser) localStorage.setItem(STORAGE_ADMIN_USER_STASH, realUser);

    // 2) Ask backend for a Demo Token (still using REAL admin token).
    const { data } = await api.post(
      "/admin/demo-preview/sessions",
      { demo_identity_id: demoIdentityId },
      { useRealToken: true },
    );

    // 3) Persist demo state and let AuthContext refresh as the identity.
    localStorage.setItem(STORAGE_DEMO_TOKEN, data.demo_token);
    localStorage.setItem(STORAGE_DEMO_IDENTITY, JSON.stringify(data.identity));
    localStorage.setItem(STORAGE_DEMO_SESSION, JSON.stringify(data.session));
    setIdentity(data.identity);
    setSession(data.session);
    return data;
  }, []);

  // Cosmetic block (Feb 2026) — clone a historical session into a
  // fresh active demo session. Backend mirrors the overlay row so the
  // resulting session restores all sandbox state of the source. The
  // admin lands directly inside the cloned demo (same /app reload as
  // startSession).
  const cloneSession = useCallback(async (sessionId) => {
    const realToken = localStorage.getItem("tsw_token");
    const realUser = localStorage.getItem("tsw_user");
    if (realToken) localStorage.setItem(STORAGE_ADMIN_TOKEN_STASH, realToken);
    if (realUser) localStorage.setItem(STORAGE_ADMIN_USER_STASH, realUser);

    const { data } = await api.post(
      `/admin/demo-preview/sessions/${sessionId}/clone`,
      {},
      { useRealToken: true },
    );

    localStorage.setItem(STORAGE_DEMO_TOKEN, data.demo_token);
    localStorage.setItem(STORAGE_DEMO_IDENTITY, JSON.stringify(data.identity));
    localStorage.setItem(STORAGE_DEMO_SESSION, JSON.stringify(data.session));
    setIdentity(data.identity);
    setSession(data.session);
    return data;
  }, []);

  const endSession = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      try { await api.delete("/admin/demo-preview/sessions/active"); } catch { /* idempotent */ }
    }
    localStorage.removeItem(STORAGE_DEMO_TOKEN);
    localStorage.removeItem(STORAGE_DEMO_IDENTITY);
    localStorage.removeItem(STORAGE_DEMO_SESSION);
    // Restore the admin's real session.
    const stashedTok = localStorage.getItem(STORAGE_ADMIN_TOKEN_STASH);
    const stashedUsr = localStorage.getItem(STORAGE_ADMIN_USER_STASH);
    if (stashedTok) {
      localStorage.setItem("tsw_token", stashedTok);
      localStorage.removeItem(STORAGE_ADMIN_TOKEN_STASH);
    }
    if (stashedUsr) {
      localStorage.setItem("tsw_user", stashedUsr);
      localStorage.removeItem(STORAGE_ADMIN_USER_STASH);
    }
    setIdentity(null);
    setSession(null);
  }, []);

  const value = useMemo(() => ({
    featureFlag,
    isActive,
    identity,
    session,
    refreshFlag,
    listIdentities,
    startSession,
    cloneSession,
    endSession,
  }), [featureFlag, isActive, identity, session, refreshFlag, listIdentities, startSession, cloneSession, endSession]);

  return (
    <DemoPreviewContext.Provider value={value}>
      {children}
    </DemoPreviewContext.Provider>
  );
};

export const useDemoPreview = () => useContext(DemoPreviewContext);
